import useTheme from 'hooks/useTheme';
import { enumColumn } from 'models/column-monitoring.model';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const initColorsSystem = {
  brandPrimary: '',
  brandSuccess: '',
  brandInfo: '',
  brandWarning: '',
  brandDanger: '',
  brandDark: '',
  brandClickedRate: '',
  brandReplied: '',
  brandConvertor: '',
  brandBlack: '',
  brandSecondary: '',
};

const ThemeContext = React.createContext({
  colorStatusReport: {
    sent: '',
    revenue: '',
    delivered: '',
    unconfirm: '',
    block: '',
    failed: '',
    replied: '',
    optout: '',
    clicked: '',
    convertor: '',
    acd: '',
  },
  colorsSystem: initColorsSystem,
  colorChart: {
    optout: '',
    failed: '',
    clicked: '',
    delivered: '',
    convertor: '',
  },
  genColorByStats: (field) => {},
  switchColorsStats: (field) => {},
});

export const useStyles = () => React.useContext(ThemeContext);

const getGlobalStyles = () => {
  const globalStyles = window.getComputedStyle(document.body);
  return globalStyles;
};

const ThemeProvider = ({ children }) => {
  const { isDarkTheme } = useTheme();
  const [colorsSystem, setColorsSystem] = useState(initColorsSystem);

  useEffect(() => {
    setColorsSystem({
      brandPrimary: getGlobalStyles().getPropertyValue('--color-primary-v2'),
      brandSuccess: getGlobalStyles().getPropertyValue('--color-success-v2'),
      brandInfo: '#63c2de',
      brandWarning: getGlobalStyles().getPropertyValue('--color-warning-v2'),
      brandDanger: getGlobalStyles().getPropertyValue('--color-danger-v2'),
      brandDark: '#2f353a',
      brandClickedRate: '#1a4d2a',
      brandReplied: '#9400D3',
      brandConvertor: '#ABABAB',
      brandBlack: '#313131',
      brandSecondary: 'rgba(0, 0, 0, 0.1)',
    });
  }, [isDarkTheme]);

  const colorStatusReport = useMemo(
    () => ({
      sent: colorsSystem.brandPrimary,
      revenue: colorsSystem.brandPrimary,
      delivered: colorsSystem.brandSuccess,
      unconfirm: '#a5aeb7',
      block: colorsSystem.brandDanger,
      failed: colorsSystem.brandWarning,
      replied: colorsSystem.brandSuccess,
      optout: colorsSystem.brandDanger,
      clicked: colorsSystem.brandSuccess,
      convertor: '#ABABAB',
      acd: '#000000',
    }),
    [colorsSystem]
  );

  const colorChart = useMemo(
    () => ({
      optout: colorStatusReport.failed,
      failed: colorStatusReport.block,
      clicked: colorStatusReport.clicked,
      delivered: colorStatusReport.delivered,
      convertor: colorStatusReport.convertor,
    }),
    [colorStatusReport]
  );

  const switchColorsStats = useCallback(
    (cellName = '') => {
      switch (cellName) {
        case enumColumn.sent:
          return colorStatusReport.sent;

        case enumColumn.delivered:
          return colorStatusReport.delivered;

        case enumColumn.callbackCount:
        case enumColumn.clicked:
        case enumColumn.convertor:
        case enumColumn.listenCounter:
        case 'callback':
        case 'listen':
        case enumColumn.ivrClickRate:
          // In general BE return callbackCount, but inside each item BE return callback
          return colorStatusReport.clicked;

        case enumColumn.replied:
          return colorStatusReport.replied;

        case enumColumn.failed:
          return colorStatusReport.failed;

        case enumColumn.optout:
        case enumColumn.ivrOptoutRate:
          return colorStatusReport.optout;

        case enumColumn.blocked:
          return colorStatusReport.block;

        case enumColumn.attemptFailed:
          return colorStatusReport.block;

        default:
          return '';
      }
    },
    [colorStatusReport]
  );

  const genColorByStats = useCallback(
    (field) => {
      const mappingColors = {
        sent: colorStatusReport.sent,
        delivered: colorStatusReport.delivered,
        clicked: colorStatusReport.clicked,
        replied: colorStatusReport.replied,
        callback: colorStatusReport.replied,
        listen: colorStatusReport.replied,
        failed: colorStatusReport.failed,
        attemptFailed: colorStatusReport.block,
        block: colorStatusReport.block,
        blocked: colorStatusReport.block,
        optout: colorStatusReport.block,
        convertor: colorStatusReport.convertor,
        revenue: colorStatusReport.revenue,
        revenuePerMil: colorStatusReport.revenue,
        ivrClickRate: colorStatusReport.clicked,
        ivrOptoutRate: colorStatusReport.block,
      };

      return mappingColors[field] || '';
    },
    [colorStatusReport]
  );

  const value = useMemo(() => {
    return {
      colorStatusReport,
      colorsSystem,
      colorChart,
      genColorByStats,
      switchColorsStats,
    };
  }, [colorStatusReport, colorsSystem, colorChart, genColorByStats, switchColorsStats]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeProvider;
